import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '../config/supabaseClient';
import { signIn, signUp, signOut, resetPassword } from '../services/authService';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);
  // `userId` records who this profile actually belongs to. Without it the
  // cache was keyed on nothing: `loaded` stayed true across an account switch
  // that did not go through signOut(), so the new user inherited the previous
  // user's name, avatar and — the dangerous part — their accountStatus, which
  // is what ProtectedRoute's suspension gate reads.
  const [profileData, setProfileData] = useState({
    userId: null,
    profileImage: '',
    fullName: '',
    phone: '',
    accountStatus: '',
    loaded: false
  });

  useEffect(() => {
    // Check active sessions and sets the user
    const initializeAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        setSession(session);
        setUser(session?.user ?? null);
        
        // Extract and store role
        const role = session?.user?.user_metadata?.role;
        setUserRole(role);
        
        // Also store in localStorage for backward compatibility
        if (session?.user) {
          localStorage.setItem('isLoggedIn', 'true');
          localStorage.setItem('userData', JSON.stringify({
            id: session.user.id,
            email: session.user.email,
            full_name: session.user.user_metadata?.full_name,
            role: session.user.user_metadata?.role,
            user_type: session.user.user_metadata?.user_type,
          }));
          // Store role separately for easy access
          if (role) {
            localStorage.setItem('role', role);
          }
        } else {
          localStorage.removeItem('isLoggedIn');
          localStorage.removeItem('userData');
          localStorage.removeItem('role');
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();

    // Listen for changes on auth state (sign in, sign out, etc.)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      // Extract and store role
      const role = session?.user?.user_metadata?.role;
      setUserRole(role);

      // Send welcome email on first verified login; login notification on subsequent logins.
      // Only trigger on SIGNED_IN — TOKEN_REFRESHED must never trigger emails because it
      // can race with SIGNED_IN and would cause welcome + notification to both fire on
      // the same session (resulting in duplicate / unexpected emails).
      if (session?.user && event === 'SIGNED_IN') {
        const emailConfirmed = session.user.email_confirmed_at || session.user.confirmed_at;
        const welcomeSentKey = `welcome_email_sent_${session.user.id}`;
        const loginNotifKey = `login_notif_ts_${session.user.id}`;

        // The /api/send-* endpoints require the caller's own access token and
        // reject a userId that is not the token's subject, so a notification
        // can never be triggered on someone else's behalf.
        const notifyHeaders = {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        };

        if (emailConfirmed && !localStorage.getItem(welcomeSentKey)) {
          // First verified login — send welcome + referral emails exactly once.
          localStorage.setItem(welcomeSentKey, 'true');
          fetch('/api/send-welcome-email', {
            method: 'POST',
            headers: notifyHeaders,
            body: JSON.stringify({ userId: session.user.id }),
          }).catch(err => console.error('Failed to send welcome email:', err));

          fetch('/api/send-referral-email', {
            method: 'POST',
            headers: notifyHeaders,
            body: JSON.stringify({ referredUserId: session.user.id }),
          }).catch(err => console.error('Failed to send referral notification email:', err));

        } else if (emailConfirmed) {
          // Subsequent logins — send login notification with a 60-second cooldown to
          // guard against SIGNED_IN firing twice in rapid succession (e.g. page reload).
          const lastNotif = parseInt(localStorage.getItem(loginNotifKey) || '0', 10);
          const now = Date.now();
          if (now - lastNotif > 60_000) {
            localStorage.setItem(loginNotifKey, String(now));
            fetch('/api/send-login-notification', {
              method: 'POST',
              headers: notifyHeaders,
              body: JSON.stringify({ userId: session.user.id }),
            }).catch(err => console.error('Failed to send login notification:', err));
          }
        }
      }
      
      // Update localStorage
      if (session?.user) {
        localStorage.setItem('isLoggedIn', 'true');
        localStorage.setItem('userData', JSON.stringify({
          id: session.user.id,
          email: session.user.email,
          full_name: session.user.user_metadata?.full_name,
          role: session.user.user_metadata?.role,
          user_type: session.user.user_metadata?.user_type,
        }));
        // Store role separately for easy access
        if (role) {
          localStorage.setItem('role', role);
        }
      } else {
        localStorage.removeItem('isLoggedIn');
        localStorage.removeItem('userData');
        localStorage.removeItem('role');
      }
      
      setLoading(false);
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  // Load profile data once and cache it
  const loadProfileData = useCallback(async (forceReload = false) => {
    if (!user?.id) return;
    
    // Return cached data only when it belongs to the user currently signed in.
    if (profileData.loaded && profileData.userId === user.id && !forceReload) {
      return profileData;
    }

    try {
      const { data, error } = await supabase
        .from('users')
        .select('profile_image, full_name, phone, account_status')
        .eq('id', user.id)
        .single();

      if (!error && data) {
        const newProfileData = {
          userId: user.id,
          profileImage: data.profile_image || '',
          fullName: data.full_name || '',
          phone: data.phone || '',
          // Drives the suspension gate in ProtectedRoute. Only an explicit
          // 'banned'/'blocked' blocks — see the fail-open note there.
          accountStatus: data.account_status || '',
          loaded: true
        };
        setProfileData(newProfileData);
        return newProfileData;
      }
    } catch (error) {
      console.error('Error loading profile data:', error);
    }
    
    return profileData;
  }, [user?.id, profileData]);

  // Load profile data when the signed-in user is set OR changes. Testing
  // `!profileData.loaded` alone meant an account switch that skipped signOut()
  // — signing in at /admin/login while a user session was live, or following an
  // email verification link in a browser that already had one — kept the
  // previous user's profile, including their accountStatus.
  useEffect(() => {
    if (user?.id && profileData.userId !== user.id) {
      loadProfileData(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, profileData.userId]);

  // Listen for profile updates
  useEffect(() => {
    const handleProfileUpdate = () => {
      loadProfileData(true);
    };
    
    window.addEventListener('profile-image-updated', handleProfileUpdate);
    
    return () => {
      window.removeEventListener('profile-image-updated', handleProfileUpdate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = {
    user,
    session,
    loading,
    userRole,
    profileData,
    loadProfileData,
    signIn: async (email, password) => {
      const result = await signIn(email, password);
      return result;
    },
    signUp: async (email, password, fullName, role, referralCode) => {
      const result = await signUp(email, password, fullName, role, referralCode);
      return result;
    },
    signOut: async () => {
      // Determine redirect path based on user role before clearing
      const currentRole = userRole || localStorage.getItem('role');
      const redirectPath = (currentRole === '4' || currentRole === 4 || currentRole === 'admin' || currentRole === '0') 
        ? '/admin/login' 
        : '/';
      
      const result = await signOut();
      setUser(null);
      setSession(null);
      setUserRole(null);
      setProfileData({
        userId: null,
        profileImage: '',
        fullName: '',
        phone: '',
        accountStatus: '',
        loaded: false
      });
      localStorage.removeItem('isLoggedIn');
      localStorage.removeItem('userData');
      localStorage.removeItem('role');
      
      return { ...result, redirectPath };
    },
    resetPassword: async (email) => {
      const result = await resetPassword(email);
      return result;
    },
    // Helper function to get role name
    getRoleName: () => {
      const roleMap = { '1': 'User', '4': 'Admin' };
      return roleMap[String(userRole)] || 'User';
    },
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
